import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Activity, AlertTriangle, Shield, Zap, Users, Clock } from "lucide-react";
import { useSystemMonitoring } from "@/hooks/useSystemMonitoring";
import MainLayout from "@/components/MainLayout";
import AdminProtectedRoute from "@/components/AdminProtectedRoute";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { format } from "date-fns";

const AdminMonitoring = () => {
  const { metrics, cspViolations, edgeFunctionPerf, rateLimitHits, loading, refresh } = useSystemMonitoring();

  // Prepare chart data
  const functionPerfChartData = edgeFunctionPerf.slice(0, 20).reverse().map((perf, index) => ({
    name: `#${index + 1}`,
    time: perf.execution_time_ms,
    function: perf.function_name,
  }));

  const getHealthStatus = () => {
    if (metrics.edgeFunctionErrors > 10) return { label: 'Critical', color: 'destructive' };
    if (metrics.cspViolations > 50 || metrics.rateLimitHits > 100) return { label: 'Warning', color: 'warning' };
    return { label: 'Healthy', color: 'default' };
  };

  const healthStatus = getHealthStatus();

  return (
    <AdminProtectedRoute>
      <MainLayout>
        <div className="container mx-auto py-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">System Monitoring</h1>
              <p className="text-muted-foreground">Real-time system health and performance metrics</p>
            </div>
            <Button onClick={refresh} disabled={loading}>
              Refresh
            </Button>
          </div>

          {/* System Health Overview */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                System Health
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Badge variant={healthStatus.color as any}>
                  {healthStatus.label}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  Last updated: {format(new Date(), 'PPpp')}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Key Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Active Users</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{metrics.activeUsers}</div>
                <p className="text-xs text-muted-foreground">Last 5 minutes</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Rate Limit Hits</CardTitle>
                <Shield className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{metrics.rateLimitHits}</div>
                <p className="text-xs text-muted-foreground">Last 24 hours</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">CSP Violations</CardTitle>
                <AlertTriangle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{metrics.cspViolations}</div>
                <p className="text-xs text-muted-foreground">Last 24 hours</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Edge Function Errors</CardTitle>
                <Zap className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{metrics.edgeFunctionErrors}</div>
                <p className="text-xs text-muted-foreground">Last 24 hours</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Avg Response Time</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{metrics.avgResponseTime}ms</div>
                <p className="text-xs text-muted-foreground">Edge functions</p>
              </CardContent>
            </Card>
          </div>

          {/* Edge Function Performance Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Edge Function Performance</CardTitle>
              <CardDescription>Response times for recent function calls</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={functionPerfChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis label={{ value: 'Time (ms)', angle: -90, position: 'insideLeft' }} />
                  <Tooltip 
                    content={({ active, payload }) => {
                      if (active && payload && payload[0]) {
                        return (
                          <div className="bg-background border rounded-lg p-2 shadow-lg">
                            <p className="font-semibold">{payload[0].payload.function}</p>
                            <p className="text-sm">{payload[0].value}ms</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar dataKey="time" fill="hsl(var(--primary))" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Recent Events Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* CSP Violations */}
            <Card>
              <CardHeader>
                <CardTitle>Recent CSP Violations</CardTitle>
                <CardDescription>Security policy violations detected</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[300px]">
                  {cspViolations.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No violations detected</p>
                  ) : (
                    <div className="space-y-3">
                      {cspViolations.map((violation) => (
                        <div key={violation.id} className="border-b pb-3 last:border-0">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="text-sm font-medium">{violation.violated_directive}</p>
                              <p className="text-xs text-muted-foreground truncate">
                                {violation.blocked_uri}
                              </p>
                            </div>
                            <Badge variant="outline" className="text-xs">
                              {format(new Date(violation.created_at), 'HH:mm')}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Rate Limit Hits */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Rate Limit Hits</CardTitle>
                <CardDescription>Blocked requests due to rate limiting</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[300px]">
                  {rateLimitHits.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No rate limit hits</p>
                  ) : (
                    <div className="space-y-3">
                      {rateLimitHits.map((hit) => (
                        <div key={hit.id} className="border-b pb-3 last:border-0">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="text-sm font-medium">{hit.event_type}</p>
                              <p className="text-xs text-muted-foreground">
                                {hit.details?.endpoint || 'Unknown endpoint'}
                              </p>
                            </div>
                            <Badge variant="outline" className="text-xs">
                              {format(new Date(hit.created_at), 'HH:mm')}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </div>
      </MainLayout>
    </AdminProtectedRoute>
  );
};

export default AdminMonitoring;
